// src/pages/ClientBranchAddPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import PhoneInput from "react-phone-number-input";
import parsePhoneNumber from "libphonenumber-js";
import "react-phone-number-input/style.css";
import CustomSelect from "../components/common/CustomSelect";
import Breadcrumb from "../components/Breadcrumb";
import { branchApi, locationApi } from "../Apis/Api";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import axios from "axios";
import { apiCall } from "../Apis/apiCall";
import PhoneNumberCountryChange from "../components/common/PhoneNumberCountryChange";

// Unified phone validation function
const validatePhoneNumber = (value) => {
  if (!value) return false;
  try {
    const phoneNumber = parsePhoneNumber(value);
    return phoneNumber && phoneNumber.isValid() && phoneNumber.country;
  } catch {
    return false;
  }
};

// Enhanced validation schema with tax_ids
const validationSchema = Yup.object().shape({
  country_id: Yup.string().required("Country is required"),
  city_id: Yup.string().required("City is required"),
  branch_name: Yup.string()
    .required("Branch name is required")
    .min(2, "Branch name must be at least 2 characters")
    .max(100, "Branch name cannot exceed 100 characters")
    .matches(
      /^[a-zA-Z0-9\s\-&.()]+$/,
      "Branch name contains invalid characters"
    ),
  address: Yup.string()
    .required("Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(255, "Address cannot exceed 255 characters"),
  phone_number: Yup.string()
    .required("Phone number is required")
    .test(
      "is-valid-phone",
      "Please enter a valid international phone number",
      validatePhoneNumber
    )
    .test("min-length", "Phone number is too short", (value) => {
      if (!value) return false;
      try {
        const phoneNumber = parsePhoneNumber(value);
        return (
          phoneNumber &&
          phoneNumber.nationalNumber &&
          phoneNumber.nationalNumber.length >= 7
        );
      } catch {
        return false;
      }
    }),
  tax_ids: Yup.array(),
});

// Component
const AddClientBranchLayer = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState("PK");
  const [code, setCode] = useState();
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      country_id: "",
      city_id: "",
      branch_name: "",
      address: "",
      phone_number: "",
      tax_ids: [],
    },
  });

  const selectedCountryId = watch("country_id");

  // Function to find country by country_code
  const findCountryByCode = (countryCode, countriesList) => {
    if (!countryCode || !countriesList || countriesList.length === 0) return null;
    
    // Convert to uppercase for case-insensitive comparison
    const upperCode = countryCode.toUpperCase();
    
    // Try exact match first
    let country = countriesList.find(c => 
      c.country_code && c.country_code.toUpperCase() === upperCode
    );
    
    // If not found, try to find by partial match
    if (!country) {
      country = countriesList.find(c => 
        c.country_code && c.country_code.toUpperCase().includes(upperCode)
      );
    }
    
    return country;
  };

  // Fetch All Countries
  const fetchCountries = async () => {
    let countryCodeFromStorage = localStorage.getItem("country_code");

    // Agar localStorage me null, "null", "" ya undefined ho, defaultCountry use karo
    const invalidValues = [null, "null", "", undefined];
    const initialCountry = invalidValues.includes(countryCodeFromStorage)
      ? "SA"
      : countryCodeFromStorage.toLowerCase();
    
    setCode(initialCountry);
    
    try {
      setCountriesLoading(true);
      const res = await locationApi.getAllCountries({
        want_pagination: 0,
        sort_by: "country_name",
        sort_order: "asc",
      });
      
      const countriesData = res.data || [];
      setCountries(countriesData);
      handleApiResponse(res, "load countries", "GET");
      
      // Find country based on country_code from localStorage
      const foundCountry = findCountryByCode(countryCodeFromStorage, countriesData);
      
      if (foundCountry) {
        // Set the country_id in the form
        setValue("country_id", String(foundCountry.country_id));
        
        // Set the country code for phone input
        setSelectedCountryCode(foundCountry.country_code || "PK");
        setCode(foundCountry.country_code || "PK");
        
        // Fetch cities and taxes for this country
        fetchCities(String(foundCountry.country_id));
        fetchTaxes(String(foundCountry.country_id));
        
        console.log("Country pre-filled:", foundCountry.country_name, foundCountry.country_id);
      } else {
        console.log("Country not found for code:", countryCodeFromStorage);
      }
    } catch (error) {
      handleApiResponse(error, "Failed to load countries", "GET");
    } finally {
      setCountriesLoading(false);
      setIsInitializing(false);
    }
  };

  // Fetch All Cities
  const fetchCities = async (countryId) => {
    try {
      setCitiesLoading(true);
      const res = await locationApi.getAllCities({
        country_id: Number(countryId),
        want_pagination: 0,
        sort_by: "city_name",
        sort_order: "asc",
      });
      setCities(res.data || []);
      handleApiResponse(res, "load cities", "GET");
    } catch (error) {
      handleApiResponse(error, "Failed to load cities", "GET");
    } finally {
      setCitiesLoading(false);
    }
  };

  // Fetch Taxes by Country
  const fetchTaxes = async (countryId) => {
    try {
      setTaxesLoading(true);
      const res = await locationApi.getTaxByCountry({
        country_id: Number(countryId),
      });
      setTaxes(res.data || []);
      handleApiResponse(res, "load taxes", "GET");
    } catch (error) {
      handleApiResponse(error, "Failed to load taxes", "GET");
    } finally {
      setTaxesLoading(false);
    }
  };

  // Initial Data Fetch on Component Mount
  useEffect(() => {
    fetchCountries();
  }, []);

  // Effect to handle country selection changes
  useEffect(() => {
    // Skip if still initializing or no country selected
    if (isInitializing || !selectedCountryId) return;
    
    if (countries.length > 0) {
      const selectedCountry = countries.find(
        (c) => String(c.country_id) === selectedCountryId
      );
      
      // Reset taxes, cities, and form fields immediately
      setTaxes([]); // Clear taxes state
      setCities([]); // Clear cities state
      setValue("city_id", "");
      setValue("phone_number", "");
      setValue("tax_ids", []);

      if (selectedCountry && selectedCountry.country_code) {
        setSelectedCountryCode(selectedCountry.country_code);
        setCode(selectedCountry.country_code); // Update country code
        fetchCities(selectedCountryId);
        fetchTaxes(selectedCountryId);
      } else {
        setSelectedCountryCode("PK");
        fetchCities(selectedCountryId);
        fetchTaxes(selectedCountryId);
      }
    } else {
      // Reset all dependent states when no country is selected
      setCities([]);
      setTaxes([]);
      setValue("city_id", "");
      setValue("phone_number", "");
      setValue("tax_ids", []);
      setSelectedCountryCode("PK");
    }
  }, [selectedCountryId, setValue, countries, isInitializing]);

  // Form Submission
  const onSubmit = async (data) => {
    try {
      setSubmit(true);
      if (!validatePhoneNumber(data.phone_number)) {
        handleCustomResponse("error", "Please enter a valid phone number");
        return;
      }

      const phoneNumber = parsePhoneNumber(data.phone_number);
      const payload = {
        ...data,
        country_id: Number(data.country_id),
        city_id: Number(data.city_id),
        phone_number: phoneNumber.format("E.164"),
        tax_ids: data.tax_ids.length > 0 ? data.tax_ids.map(Number) : [],
      };

      const res = await branchApi.addClientBranch(payload);
      handleApiResponse(res, "Branch created successfully!", "POST");

      reset();
      navigate(-1);
    } catch (error) {
      handleApiResponse(error, "Failed to create branch", "POST");
    } finally {
      setSubmit(false);
    }
  };

  // Options for Select components
  const countryOptions = countries.map((c) => ({
    value: String(c.country_id),
    label: c.country_name,
  }));

  const filteredCityOptions = cities
    .map((c) => ({
      value: String(c.city_id),
      label: c.city_name,
    }))
    .filter((c) => String(c.value));

  return (
    <>
      <Breadcrumb title="Add Branch" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row g-20">
              {/* Country */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Country *</label>
                <Controller
                  name="country_id"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      name="country_id"
                      value={countryOptions.find(option => option.value === field.value) || null}
                      onChange={(selected) => {
                        field.onChange(selected?.value || "");
                        const selectedCountry = countries.find(
                          (c) => String(c.country_id) === selected?.value
                        );
                        if (selectedCountry && selectedCountry.country_code) {
                          setSelectedCountryCode(selectedCountry.country_code);
                        } 
                      }}
                      options={countryOptions}
                      placeholder="Select Country"
                      isClearable
                      isLoading={countriesLoading}
                    />
                  )}
                />
                {errors.country_id && (
                  <span className="text-danger-600">
                    {errors.country_id.message}
                  </span>
                )}
              </div>

              {/* City */}
              <div className="col-md-6 mb-20">
                <label className="form-label">City *</label>
                <Controller
                  name="city_id"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      name="city_id"
                      value={filteredCityOptions.find(option => option.value === field.value) || null}
                      onChange={(selected) =>
                        field.onChange(selected?.value || "")
                      }
                      options={filteredCityOptions}
                      placeholder="Select City"
                      isDisabled={!selectedCountryId}
                      isClearable
                      isLoading={citiesLoading}
                    />
                  )}
                />
                {errors.city_id && (
                  <span className="text-danger-600">
                    {errors.city_id.message}
                  </span>
                )}
              </div>

              {/* Branch Name */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Branch Name *</label>
                <input
                  {...register("branch_name")}
                  className={`form-control radius-8 ${
                    errors.branch_name ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Branch Name"
                />
                {errors.branch_name && (
                  <span className="text-danger-600">
                    {errors.branch_name.message}
                  </span>
                )}
              </div>

              {/* Phone Number */}
              <div className="col-md-6 mb-20">
                <PhoneNumberCountryChange
                  name="phone_number"
                  control={control}
                  errors={errors}
                  defaultCountry={code}
                  onChange={(value) => {
                    setValue("phone_number", value || "");
                    if (value) {
                      const country = parsePhoneNumber(value)?.country;
                      if (country) setSelectedCountryCode(country);
                    }
                  }}
                />
              </div>

              {/* Address */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Address *</label>
                <input
                  {...register("address")}
                  className={`form-control radius-8 ${
                    errors.address ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Address"
                />
                {errors.address && (
                  <span className="text-danger-600">
                    {errors.address.message}
                  </span>
                )}
              </div>

              {/* Taxes Checkboxes */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Taxes *</label>
                <div className="d-flex flex-column gap-2">
                  {taxesLoading ? (
                    <span>Loading taxes...</span>
                  ) : taxes.length > 0 ? (
                    taxes.map((tax) => (
                      <label
                        key={tax.tax_id}
                        className="d-flex align-items-center gap-2"
                      >
                        <Controller
                          name="tax_ids"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="checkbox"
                              value={tax.tax_id}
                              checked={field.value?.includes(
                                Number(tax.tax_id)
                              )}
                              onChange={(e) => {
                                const currentValues = field.value || [];
                                let updatedValues;
                                if (e.target.checked) {
                                  updatedValues = [
                                    ...currentValues,
                                    Number(e.target.value),
                                  ];
                                } else {
                                  updatedValues = currentValues.filter(
                                    (id) => id !== Number(e.target.value)
                                  );
                                }
                                field.onChange(
                                  updatedValues.length > 0 ? updatedValues : []
                                );
                              }}
                              disabled={!selectedCountryId}
                              className="form-check-input"
                            />
                          )}
                        />
                        {`${tax.tax_type_name} (${tax.tax_percent}%)`}
                      </label>
                    ))
                  ) : (
                    <span>No taxes available</span>
                  )}
                </div>
                {errors.tax_ids && (
                  <span className="text-danger-600">
                    {errors.tax_ids.message}
                  </span>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-center gap-3">
              <CancelButtonForm />
              <UpdateButtonForm disabled={submit} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddClientBranchLayer;